import { getRestApiBaseUrl } from "../utils/restApiBaseUrl"
import { getVisitorId } from "./visitorId"

/**
 * Evento recebido do stream SSE de pedidos.
 * Espelha o payload publicado pelo backend (EventosTempoRealService).
 *
 * Dois formatos trafegam no mesmo canal:
 *  - Evento LEVE de status/novo pedido: { estabelecimentoId, pedidoId, status, tipo }.
 *  - Evento de IMPRESSÃO (kind === "IMPRESSAO"): carrega o payload completo da
 *    comanda (mesma shape do EventosNaoRecebidosRetorno da fila) para imprimir em
 *    tempo real sem drenar a fila.
 */
export type EventoPedido = {
    estabelecimentoId: number
    pedidoId: number
    status: number | null
    tipo: string // "PLACED" (novo pedido) | "STATUS" (troca de status)
    // Presentes apenas quando kind === "IMPRESSAO":
    kind?: string
    id?: number // SolicitacaoImpressao.id — chave de dedup
    code?: string // "PLACED" | "PRINT" | "PRINT1..n" | "CUPOM_FISCAL" | ...
    correlationId?: number // id do pedido
    texto?: string
    quantidadeVias?: number
    statusPedido?: number | null
    type?: string | null // tipoPedido: "0" delivery | "1" retirada | "2" mesa
    plataforma?: string | null
    didiOrderUUID?: string | null
}

/**
 * Monta a URL do endpoint SSE. O SSE fica em /sse/pedidos (fora de /rest/),
 * então derivamos a origem a partir da base REST.
 *
 * NÃO enviamos `estabelecimento`: o backend usa o modo "login", autenticando pela
 * `auth` e assinando TODOS os estabelecimentos do fornecedor. Assim o stream nunca
 * depende de um id de estabelecimento que possa chegar obsoleto/de outra conta — a
 * lista vem sempre do fornecedor autenticado. Cada tela filtra os eventos pelo
 * `estabelecimentoId` que lhe interessa (a Pedidos do Dia, por ser por
 * estabelecimento; o Recebedor consome todos, pois já lista a conta inteira).
 *
 * Enviamos também `navegador` = visitorId (UUID único e estável por navegador,
 * o mesmo do header X-Visitor-Id das chamadas REST). O EventSource não permite
 * headers customizados, então vai como query param. É o que permite ao backend
 * distinguir CADA navegador conectado na tela de Conexões — sem ele, todas as
 * conexões da mesma conta (mesmo `clienteId`) ficam indistinguíveis.
 */
function montarUrl(autorizacao: string): string {
    const base = getRestApiBaseUrl() // ex.: https://ws.appclientefiel.com.br/rest/
    const origem = base.replace(/rest\/?$/, "") // -> https://ws.appclientefiel.com.br/
    const params = new URLSearchParams({
        auth: autorizacao || "",
        navegador: getVisitorId(),
    })
    return `${origem}sse/pedidos?${params.toString()}`
}

// [SSE-DEBUG] Log temporário para diagnosticar conexão em localhost/pré-produção.
// Não imprime o token de auth (só a origem). REMOVER quando terminar o diagnóstico.
function logSSE(...args: unknown[]): void {
    // eslint-disable-next-line no-console
    console.log(
        `%c[SSE ${new Date().toLocaleTimeString()}]`,
        "color:#2e9e5b;font-weight:bold",
        ...args
    )
}

/** Reconexão: backoff exponencial 1s → 2s → 4s… com teto. */
const RECONEXAO_DELAY_INICIAL_MS = 1000
const RECONEXAO_DELAY_MAX_MS = 30000
/**
 * Watchdog de inatividade: se nenhum evento (nem o `ping` de heartbeat) chegar
 * dentro desta janela, a conexão é considerada "zumbi" e reaberta. Deve ser
 * confortavelmente maior que o heartbeat do servidor (25s) para não disparar em
 * operação normal.
 */
const INATIVIDADE_MAX_MS = 60000
const WATCHDOG_INTERVALO_MS = 15000

/**
 * Abre o stream SSE de pedidos da conta autenticada (modo login: todos os
 * estabelecimentos do fornecedor), com reconexão automática.
 *
 * O auto-retry nativo do EventSource só cobre quedas de rede (readyState CONNECTING).
 * Quando o servidor reinicia, o proxy costuma responder HTTP 502/503 por alguns
 * segundos e o browser encerra a conexão DE VEZ (readyState CLOSED), sem tentar de
 * novo — o stream ficaria morto até um reload. Aqui tratamos isso reabrindo
 * manualmente (com backoff) nesse caso, além de:
 *   - um watchdog que reabre conexões "zumbi" (TCP meio-aberto que nunca dispara
 *     onerror), medido pela chegada do evento `ping` de heartbeat do servidor;
 *   - gatilhos de reconexão imediata quando a aba volta ao foco (`visibilitychange`)
 *     ou a rede volta (`online`) — cobre restart de servidor com aba em segundo
 *     plano e notebook saindo do sleep.
 *
 * `onErro` é apenas informativo. Retorna uma função para fechar a conexão (e
 * cancelar toda a maquinaria de reconexão).
 */
export function abrirStreamPedidos(
    autorizacao: string,
    onEvento: (evento: EventoPedido) => void,
    onErro?: (e: Event) => void,
    /**
     * Chamado quando o stream REABRE (nunca na primeira conexão). Serve para o consumidor
     * recuperar o que passou durante a queda: pub/sub não tem replay, então todo push
     * emitido enquanto a conexão esteve fora está perdido. Este serviço não conhece o
     * store — quem decide o que buscar é quem passa o callback.
     */
    onReaberto?: () => void
): () => void {
    if (typeof window === "undefined" || typeof window.EventSource === "undefined") {
        // Ambiente sem suporte a EventSource: no-op.
        return () => {}
    }

    const url = montarUrl(autorizacao)

    let es: EventSource | null = null
    let backoffTimer: ReturnType<typeof setTimeout> | null = null
    let watchdog: ReturnType<typeof setInterval> | null = null
    let tentativas = 0
    let ultimoEventoEm = Date.now()
    let fechado = false
    // Distingue a primeira conexão de uma REabertura: não faz sentido disparar catch-up
    // logo ao entrar na tela, quando nada foi perdido ainda.
    let jaAbriuAlgumaVez = false

    const marcarVivo = () => {
        ultimoEventoEm = Date.now()
    }

    const handleMessage = (ev: MessageEvent) => {
        marcarVivo()
        try {
            const evento = JSON.parse(ev.data) as EventoPedido
            logSSE("📦 evento recebido:", evento) // [SSE-DEBUG]
            onEvento(evento)
        } catch {
            logSSE("✉️ mensagem não-JSON:", ev.data) // [SSE-DEBUG]
            /* ignora mensagens não-JSON (ex.: comentários de heartbeat não chegam como message) */
        }
    }

    const fecharEs = () => {
        if (!es) return
        try {
            es.close()
        } catch {
            /* ignore */
        }
        es = null
    }

    const abrir = () => {
        if (fechado) return
        fecharEs()
        // [SSE-DEBUG] Origem sem o token de auth.
        logSSE("🔌 abrindo conexão →", url.replace(/auth=[^&]*/, "auth=***"))
        es = new EventSource(url)

        es.onopen = () => {
            tentativas = 0
            marcarVivo()
            logSSE("✅ conectado (readyState OPEN)") // [SSE-DEBUG]
            if (jaAbriuAlgumaVez) {
                try {
                    onReaberto?.()
                } catch {
                    /* o catch-up é best-effort; não pode derrubar a conexão */
                }
            }
            jaAbriuAlgumaVez = true
        }

        // O backend envia `event: pedido`; ouvimos também "message" por segurança.
        es.addEventListener("pedido", handleMessage as EventListener)
        es.onmessage = handleMessage
        // Heartbeat observável (event: ping): só renova o watchdog, não é um pedido.
        es.addEventListener("ping", (() => {
            marcarVivo()
            logSSE("💓 ping (heartbeat)") // [SSE-DEBUG]
        }) as EventListener)

        es.onerror = e => {
            if (onErro) onErro(e)
            // [SSE-DEBUG] readyState: 0=CONNECTING (retry nativo), 2=CLOSED (desistiu → 401,
            // 502/503, DNS…). Se cair em CLOSED logo na 1ª tentativa, suspeite da auth.
            const rs = es?.readyState
            logSSE(
                "❌ erro (readyState",
                rs,
                rs === EventSource.CLOSED ? "= CLOSED — reabrindo com backoff)" : "= CONNECTING)"
            )
            // readyState CLOSED = o browser desistiu (ex.: 502/503 no restart). Como
            // ele NÃO tentará de novo, reabrimos manualmente com backoff. Se estiver
            // CONNECTING, o retry nativo cuida — não duplicamos.
            if (es && es.readyState === EventSource.CLOSED) {
                agendarReconexao()
            }
        }
    }

    const agendarReconexao = () => {
        if (fechado || backoffTimer) return
        const delay = Math.min(
            RECONEXAO_DELAY_MAX_MS,
            RECONEXAO_DELAY_INICIAL_MS * Math.pow(2, tentativas)
        )
        tentativas++
        backoffTimer = setTimeout(() => {
            backoffTimer = null
            abrir()
        }, delay)
    }

    const reconectarAgora = () => {
        if (fechado) return
        if (backoffTimer) {
            clearTimeout(backoffTimer)
            backoffTimer = null
        }
        abrir()
    }

    // Aba voltou ao foco ou rede voltou: se o stream não está aberto, ou está
    // vencido pelo watchdog, reconecta já (zerando o backoff para não esperar).
    const aoVisivelOuOnline = () => {
        if (fechado) return
        const vencido = Date.now() - ultimoEventoEm > INATIVIDADE_MAX_MS
        if (!es || es.readyState !== EventSource.OPEN || vencido) {
            tentativas = 0
            reconectarAgora()
        }
    }

    const aoVisibilityChange = () => {
        if (document.visibilityState === "visible") aoVisivelOuOnline()
    }

    document.addEventListener("visibilitychange", aoVisibilityChange)
    window.addEventListener("online", aoVisivelOuOnline)

    watchdog = setInterval(() => {
        if (!fechado && Date.now() - ultimoEventoEm > INATIVIDADE_MAX_MS) {
            reconectarAgora()
        }
    }, WATCHDOG_INTERVALO_MS)

    abrir()

    return () => {
        fechado = true
        if (backoffTimer) {
            clearTimeout(backoffTimer)
            backoffTimer = null
        }
        if (watchdog) {
            clearInterval(watchdog)
            watchdog = null
        }
        document.removeEventListener("visibilitychange", aoVisibilityChange)
        window.removeEventListener("online", aoVisivelOuOnline)
        fecharEs()
    }
}
